"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSecondsSince = exports.getMillisecondsSince = exports.getDbTimestamp = exports.now = exports.isValidDate = exports.isSameDay = exports.getHourFromDate = exports.getStartOfLastMonth = exports.getStartOfMonth = exports.getStartOfDay = exports.parseDateTimeInput = exports.formatForDateTimeInput = exports.getSecondsUntilExpiration = exports.formatCountdown = exports.getRelativeTime = exports.formatForDisplay = exports.getMinutesFromNow = exports.isExpired = exports.resetDisplayDefaults = exports.setDisplayDefaults = exports.getDisplayDefaults = exports.getCurrentIsoString = exports.toIsoString = exports.getFutureDate = exports.getCurrentDate = exports.TimestampService = void 0;
/**
 * Centralized Timestamp Service for zlinkx
 *
 * This service provides consistent timestamp handling across the application.
 * For database operations with PostgreSQL timestamp columns, use Date objects.
 * For API responses, ISO strings are preferred.
 */
class TimestampService {
    // Single source of truth for UI display defaults.
    // Montreal uses IANA zone America/Toronto so web/mobile render the same local time.
    static DEFAULT_DISPLAY_DEFAULTS = {
        locale: 'en-US',
        timeZone: 'America/Toronto',
    };
    static displayDefaults = {
        ...TimestampService.DEFAULT_DISPLAY_DEFAULTS,
    };
    /**
     * Get centralized display defaults used by formatters
     */
    static getDisplayDefaults() {
        return { ...this.displayDefaults };
    }
    /**
     * Set centralized display defaults used by formatters
     */
    static setDisplayDefaults(defaults) {
        this.displayDefaults = {
            ...this.displayDefaults,
            ...defaults,
        };
    }
    /**
     * Reset centralized display defaults to project defaults
     */
    static resetDisplayDefaults() {
        this.displayDefaults = {
            ...this.DEFAULT_DISPLAY_DEFAULTS,
        };
    }
    /**
     * Get current date for database operations
     * Use this for setting timestamps on INSERT/UPDATE operations
     */
    static getCurrentDate() {
        return new Date();
    }
    /**
     * Get future date for expiration calculations
     * @param minutesFromNow - Minutes from current time
     */
    static getFutureDate(minutesFromNow) {
        return new Date(Date.now() + minutesFromNow * 60 * 1000);
    }
    /**
     * Convert Date to ISO string (for API responses)
     * @param date - Date object to convert
     */
    static toIsoString(date) {
        return date.toISOString();
    }
    /**
     * Get current timestamp as ISO string
     * Convenience method for API responses
     */
    static getCurrentIsoString() {
        return new Date().toISOString();
    }
    /**
     * Check if date is expired (in the past)
     * @param date - Date to check
     */
    static isExpired(date) {
        return date.getTime() < Date.now();
    }
    /**
     * Get minutes until a date
     * @param date - Target date
     * @returns Minutes from now (negative if in the past)
     */
    static getMinutesFromNow(date) {
        return Math.floor((date.getTime() - Date.now()) / (60 * 1000));
    }
    /**
     * Format date for display (frontend utility)
     * @param date - Date to format
     * @param options - Formatting options
     */
    static formatForDisplay(date, options = {}) {
        const defaults = this.getDisplayDefaults();
        const { locale = defaults.locale, timeZone = defaults.timeZone, style = 'medium' } = options;
        const dateStyle = style === 'short' ? 'short' : style === 'long' ? 'long' : 'medium';
        const timeStyle = style === 'short' ? 'short' : style === 'long' ? 'medium' : 'short';
        return date.toLocaleString(locale, {
            timeZone,
            dateStyle,
            timeStyle,
        });
    }
    /**
     * Get relative time string (e.g., "in 5 minutes", "2 hours ago")
     * @param date - Date to compare against now
     */
    static getRelativeTime(date) {
        const minutes = this.getMinutesFromNow(date);
        const absMinutes = Math.abs(minutes);
        if (absMinutes < 1) {
            return 'now';
        }
        else if (absMinutes < 60) {
            return minutes > 0 ? `in ${absMinutes} minutes` : `${absMinutes} minutes ago`;
        }
        else if (absMinutes < 1440) {
            // 24 hours
            const hours = Math.floor(absMinutes / 60);
            return minutes > 0 ? `in ${hours} hours` : `${hours} hours ago`;
        }
        else {
            const days = Math.floor(absMinutes / 1440);
            return minutes > 0 ? `in ${days} days` : `${days} days ago`;
        }
    }
    /**
     * Format countdown timer
     * @param expiresAt - Date when something expires
     * @returns Countdown string like "2h 30m remaining" or "Expired"
     */
    static formatCountdown(expiresAt) {
        const secondsLeft = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
        if (secondsLeft <= 0)
            return 'Expired';
        const hours = Math.floor(secondsLeft / 3600);
        const minutes = Math.floor((secondsLeft % 3600) / 60);
        const seconds = secondsLeft % 60;
        if (hours > 0) {
            return `${hours}h ${minutes}m remaining`;
        }
        if (minutes > 0) {
            return `${minutes}m ${seconds}s remaining`;
        }
        return `${seconds}s remaining`;
    }
    /**
     * Get seconds until expiration
     * @param expiresAt - Date when something expires
     * @returns Seconds remaining (0 if already expired)
     */
    static getSecondsUntilExpiration(expiresAt) {
        return Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
    }
    /**
     * Format for HTML datetime-local input
     * @param date - Date to format
     * @returns ISO string for datetime-local input (YYYY-MM-DDTHH:mm)
     */
    static formatForDateTimeInput(date) {
        return date.toISOString().slice(0, 16);
    }
    /**
     * Parse HTML datetime-local input
     * @param dateTimeValue - Value from datetime-local input
     * @returns Date object
     */
    static parseDateTimeInput(dateTimeValue) {
        // HTML date inputs emit YYYY-MM-DD without timezone.
        // Parsing that with new Date(string) treats it as UTC and can shift
        // display to the previous day in local timezones (e.g. 7:00 PM).
        // For date-only input, construct a local-midnight Date explicitly.
        const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateTimeValue);
        if (dateOnlyMatch) {
            const [, year, month, day] = dateOnlyMatch;
            const parsedYear = Number(year);
            const parsedMonthIndex = Number(month) - 1;
            const parsedDay = Number(day);
            return new Date(parsedYear, parsedMonthIndex, parsedDay, 0, 0, 0, 0);
        }
        return new Date(dateTimeValue);
    }
    /**
     * Get start of day
     * @param date - Reference date
     * @returns Date at start of day (00:00:00)
     */
    static getStartOfDay(date) {
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        return startOfDay;
    }
    /**
     * Get start of month
     * @param date - Reference date
     * @returns Date at start of month
     */
    static getStartOfMonth(date) {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }
    /**
     * Get start of last month
     * @param date - Reference date
     * @returns Date at start of previous month
     */
    static getStartOfLastMonth(date) {
        return new Date(date.getFullYear(), date.getMonth() - 1, 1);
    }
    /**
     * Get hour from date
     * @param date - Date to extract hour from
     * @returns Hour (0-23)
     */
    static getHourFromDate(date) {
        return date.getHours();
    }
    /**
     * Check if two dates are on the same day
     * @param date1 - First date
     * @param date2 - Second date
     * @returns True if same day
     */
    static isSameDay(date1, date2) {
        return (date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate());
    }
    /**
     * Type guard for valid Date objects
     * @param value - Value to validate
     * @returns true if valid Date
     */
    static isValidDate(value) {
        return value instanceof Date && !isNaN(value.getTime());
    }
    /**
     * Get current timestamp in milliseconds since epoch
     * Use this for performance timing, unique IDs, or timestamp comparisons
     * Prefer this over Date.now() to maintain consistent timestamp handling
     */
    static now() {
        return Date.now();
    }
    /**
     * Get current date for database timestamp columns (PostgreSQL TIMESTAMPTZ)
     * Returns a Date object suitable for Knex/PostgreSQL timestamp columns
     * Use this for setting created_at, updated_at, etc. in INSERT/UPDATE operations
     */
    static getDbTimestamp() {
        return new Date();
    }
    /**
     * Get milliseconds elapsed since a given date
     * @param date - Reference date
     * @returns Milliseconds elapsed (positive if date is in past)
     */
    static getMillisecondsSince(date) {
        return Date.now() - date.getTime();
    }
    /**
     * Get seconds elapsed since a given date
     * @param date - Reference date
     * @returns Whole seconds elapsed (positive if date is in past)
     */
    static getSecondsSince(date) {
        return Math.floor((Date.now() - date.getTime()) / 1000);
    }
}
exports.TimestampService = TimestampService;
// Export utility functions for direct use
exports.getCurrentDate = TimestampService.getCurrentDate.bind(TimestampService);
exports.getFutureDate = TimestampService.getFutureDate.bind(TimestampService);
exports.toIsoString = TimestampService.toIsoString.bind(TimestampService);
exports.getCurrentIsoString = TimestampService.getCurrentIsoString.bind(TimestampService);
exports.getDisplayDefaults = TimestampService.getDisplayDefaults.bind(TimestampService);
exports.setDisplayDefaults = TimestampService.setDisplayDefaults.bind(TimestampService);
exports.resetDisplayDefaults = TimestampService.resetDisplayDefaults.bind(TimestampService);
exports.isExpired = TimestampService.isExpired.bind(TimestampService);
exports.getMinutesFromNow = TimestampService.getMinutesFromNow.bind(TimestampService);
exports.formatForDisplay = TimestampService.formatForDisplay.bind(TimestampService);
exports.getRelativeTime = TimestampService.getRelativeTime.bind(TimestampService);
exports.formatCountdown = TimestampService.formatCountdown.bind(TimestampService);
exports.getSecondsUntilExpiration = TimestampService.getSecondsUntilExpiration.bind(TimestampService);
exports.formatForDateTimeInput = TimestampService.formatForDateTimeInput.bind(TimestampService);
exports.parseDateTimeInput = TimestampService.parseDateTimeInput.bind(TimestampService);
exports.getStartOfDay = TimestampService.getStartOfDay.bind(TimestampService);
exports.getStartOfMonth = TimestampService.getStartOfMonth.bind(TimestampService);
exports.getStartOfLastMonth = TimestampService.getStartOfLastMonth.bind(TimestampService);
exports.getHourFromDate = TimestampService.getHourFromDate.bind(TimestampService);
exports.isSameDay = TimestampService.isSameDay.bind(TimestampService);
exports.isValidDate = TimestampService.isValidDate.bind(TimestampService);
exports.now = TimestampService.now.bind(TimestampService);
exports.getDbTimestamp = TimestampService.getDbTimestamp.bind(TimestampService);
exports.getMillisecondsSince = TimestampService.getMillisecondsSince.bind(TimestampService);
exports.getSecondsSince = TimestampService.getSecondsSince.bind(TimestampService);
